import * as Sentry from "@sentry/react";
import { Component, ReactElement, ReactNode } from "react";

import { isSentryEnabled } from "@/utils/sentry";

type ErrorBoundaryFallbackProps = {
  error: unknown;
  resetError: () => void;
};

type ErrorBoundaryProps = {
  boundaryName?: string;
  children: ReactNode;
  fallback: (props: ErrorBoundaryFallbackProps) => ReactElement;
};

type ReactErrorBoundaryState = {
  error: unknown | null;
};

class ReactErrorBoundary extends Component<
  Pick<ErrorBoundaryProps, "children" | "fallback">,
  ReactErrorBoundaryState
> {
  state: ReactErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: unknown): ReactErrorBoundaryState {
    return { error };
  }

  resetError = () => {
    this.setState({ error: null });
  };

  render() {
    if (this.state.error) {
      return this.props.fallback({
        error: this.state.error,
        resetError: this.resetError,
      });
    }

    return this.props.children;
  }
}

function ErrorBoundary({
  boundaryName,
  children,
  fallback,
}: ErrorBoundaryProps) {
  if (!isSentryEnabled()) {
    return (
      <ReactErrorBoundary fallback={fallback}>{children}</ReactErrorBoundary>
    );
  }

  return (
    <Sentry.ErrorBoundary
      beforeCapture={(scope) => {
        if (boundaryName) {
          scope.setTag("error_boundary", boundaryName);
        }
      }}
      fallback={fallback}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
}

export { ErrorBoundary };
export type { ErrorBoundaryFallbackProps };
